import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Layers,
  Clock,
  ShieldCheck,
  Upload,
  ArrowRight,
  Settings,
  Database
} from 'lucide-react';

interface WellSkyStatusData {
  isConfigured: boolean;
  username: string;
  serviceProviderContext: string;
  focusId: string;
  connectionStatus: 'CONNECTED' | 'NEEDS_ATTENTION' | 'DISCONNECTED';
  statusMessage: string;
  sessionActive: boolean;
  sessionExpiresAt: string | null;
  lastSuccessfulRetrieval: string | null;
  lastRetrievalType: 'SCHEDULED' | 'MANUAL' | null;
  lastSourceFilenames: string[];
  lastImportedCount: number;
  lastDuplicateCount: number;
  lastHeldCount: number;
  lastErrorCount: number;
  autoNightlyEnabled: boolean;
  nightlyScheduleTime: string;
}

interface WellSkySyncBannerProps {
  onSyncComplete?: () => void;
  onOpenManualModal?: () => void;
  onNavigateToSettings?: () => void;
  compact?: boolean;
}

export const WellSkySyncBanner: React.FC<WellSkySyncBannerProps> = ({
  onSyncComplete,
  onOpenManualModal,
  onNavigateToSettings,
  compact = false
}) => {
  const [status, setStatus] = useState<WellSkyStatusData | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<any | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/wellsky-sync/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Error fetching WellSky status:', err);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleRetrieve = async () => {
    try {
      setSyncing(true);
      setErrorMsg(null);
      setLastSyncResult(null);

      const res = await fetch('/api/wellsky-sync/retrieve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'WellSky retrieval failed');
      }

      setLastSyncResult(data);
      setStatus(data.status);
      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (err: any) {
      console.error('Retrieve from WellSky failed:', err);
      setErrorMsg(err.message || 'Retrieval failed');
    } finally {
      setSyncing(false);
    }
  };

  const isConnected = status?.connectionStatus === 'CONNECTED';

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white shadow-xs p-3 sm:p-3.5 space-y-2.5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <div className="flex items-center space-x-2.5 min-w-0">
          <div className="relative shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping opacity-40" />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-[11.5px] font-bold uppercase tracking-wider text-slate-800">
                WellSky Source Ingestion
              </span>
              <span className="text-[9.5px] font-mono px-2 py-0.5 rounded-full font-bold border bg-emerald-50 text-emerald-800 border-emerald-200 leading-tight">
                Ready for Report 11281
              </span>
              <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200 leading-tight">
                FOCUS ID: 115157
              </span>
              <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200 leading-tight">
                Vendor ID: 359577
              </span>
            </div>
            <p className="text-[10.5px] text-slate-500 mt-0.5 leading-tight">
              Source: <strong className="text-slate-800">Report 11281 (rp_custom_AuthorizationsWithAuthID.csv)</strong> • Strict duplication-safe pipeline
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {onNavigateToSettings && (
            <button
              onClick={onNavigateToSettings}
              className="p-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 shadow-xs transition-colors cursor-pointer"
              title="Admin Connections Settings"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            id="btn-import-wellsky-file"
            onClick={onOpenManualModal}
            className="px-3.5 py-1.5 rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-all shadow-xs bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 active:scale-[0.98] cursor-pointer"
          >
            <Upload className="w-3.5 h-3.5 text-white" />
            <span>Import Report 11281 CSV</span>
          </button>
        </div>
      </div>

      {errorMsg && (
        <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{errorMsg}</span>
          </div>
          {onNavigateToSettings && (
            <button
              onClick={onNavigateToSettings}
              className="text-[11px] underline hover:text-rose-950 font-medium cursor-pointer"
            >
              Configure Credentials
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 pt-1.5 border-t border-slate-100 text-xs">
        <div className="px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200/90">
          <div className="text-[9.5px] text-slate-500 uppercase tracking-wider font-semibold">Last Successful</div>
          <div className="font-mono text-slate-800 font-semibold text-[11px] truncate mt-0.5">
            {status?.lastSuccessfulRetrieval
              ? new Date(status.lastSuccessfulRetrieval).toLocaleTimeString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : 'None Yet'}
          </div>
        </div>

        <div className="px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200/90">
          <div className="text-[9.5px] text-slate-500 uppercase tracking-wider font-semibold">Ingestion Source</div>
          <div className="font-mono text-[11px] font-bold mt-0.5 truncate">
            <span className="text-slate-800">Manual Portal Export</span>
          </div>
        </div>

        <div className="px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200/80">
          <div className="text-[9.5px] text-emerald-800 uppercase tracking-wider font-semibold">Imported</div>
          <div className="font-mono text-emerald-900 font-bold text-sm mt-0.5 leading-none">
            {status?.lastImportedCount ?? 0}
          </div>
        </div>

        <div className="px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200/90">
          <div className="text-[9.5px] text-slate-500 uppercase tracking-wider font-semibold">Duplicates</div>
          <div className="font-mono text-slate-800 font-bold text-sm mt-0.5 leading-none">
            {status?.lastDuplicateCount ?? 0}
          </div>
        </div>

        <div className="px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200/80">
          <div className="text-[9.5px] text-amber-800 uppercase tracking-wider font-semibold">Held / Quarantined</div>
          <div className="font-mono text-amber-900 font-bold text-sm mt-0.5 leading-none">
            {status?.lastHeldCount ?? 0}
          </div>
        </div>

        <div className="px-2.5 py-1.5 rounded-lg bg-rose-50 border border-rose-200/80">
          <div className="text-[9.5px] text-rose-800 uppercase tracking-wider font-semibold">Errors</div>
          <div className="font-mono text-rose-900 font-bold text-sm mt-0.5 leading-none">
            {status?.lastErrorCount ?? 0}
          </div>
        </div>
      </div>

      {status?.lastSourceFilenames && status.lastSourceFilenames.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[10.5px] text-slate-500 pt-0.5">
          <span className="text-[9.5px] font-semibold text-slate-500 uppercase tracking-wider">
            Source Files:
          </span>
          {status.lastSourceFilenames.map((f, i) => (
            <span
              key={i}
              className="font-mono text-[9.5px] px-2 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200"
            >
              {f}
            </span>
          ))}
        </div>
      )}

      {lastSyncResult && (
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{lastSyncResult.message}</span>
          </div>
          <span className="text-[10px] font-mono text-emerald-700">
            {new Date(lastSyncResult.retrievedAt).toLocaleTimeString()}
          </span>
        </div>
      )}
    </div>
  );
};
